/** tm/pages/GovTargetReview.tsx — 「세부업종 · 전용 대상」 자동 체크 검토 (2026-10-05f 지도사 「체크된 거는 내가 한 번 더 추려서 검토할 수 있도록 보여줘」 · 2026-10-05g 재설계)
 *
 *  정부지원사업 › 등록된 공고 › 「세부업종 · 전용 대상 검토」. AI 판정(공고문 전체를 읽음 · 2026-10-05o) · 사업명 · 본문 · 옛 분류에서 켠 세부업종(「제조업 › 소방·안전」)과
 *  전용 대상(기업 유형 · 특수 자격)을 두 묶음으로 근거 인용문 · 확신과 함께 보여 주고
 *  한 줄씩 추린다 — 맞음(Y) · 라벨 하나 빼기(칩 ✕ 또는 숫자 1~9) · 모두 아님(X) · AI 다시 읽기(A) · ↑↓ 이동 · Enter 공고 열기 · 되돌리기(토스트 · Z).
 *  낱말 판정이 잡았지만 AI 가 확인하지 않은 값은 「참고」로 흐리게 — 켜져 있지 않다(누르면 켤 수 있다).
 *  저장은 바로(POST /gov-targets/:id) — 매칭(로움 · fphub)에 곧바로 반영된다. 분석 대기 고르기(검토함)와 같은 손놀림.
 *
 *  속도 (2026-10-05r 지도사 「여기서 칸을 클릭하니까 한참 대기 상태에 있어」 · 「속도가 빨라야 해 — 모든 작업에서」)
 *   · 누르면 바로 화면에 반영(낙관적 갱신 — components/GovTargetReviewRow applyReviewAction) → 서버 저장은 뒤에서, 응답이 오면 그 값으로 맞춘다 · 실패하면 목록을 다시 읽어 되돌리고 알림
 *   · 같은 공고의 저장은 차례대로(줄마다 줄 세우기) — 빠르게 여러 번 눌러도 순서가 섞이지 않는다
 *   · 줄은 React.memo — 한 줄을 고치면 그 줄만 다시 그린다 · 화면에 보이는 줄(+위아래 여유)만 그린다(높이를 재 가며 창 그리기)
 *   · 알림 · 되돌리기는 따로 떨어진 부품 — 알림이 뜨고 사라져도 목록은 다시 그리지 않는다
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { C, T, R } from '../components/ui';
import * as api from '../api';
import type { GovTargetRow, GovTargetSnap } from '../types';
import { GovTopBar } from '../components/GovFeedParts';
import { CONF_LABEL, targetText } from '../components/GovTargetChips';
import { ReviewRow, applyReviewAction, snapOf, isPendingHint, estimateRowHeight, type ReviewHandlers, type ReviewBody, type ReviewStatus } from '../components/GovTargetReviewRow';

interface Props {
  topLeft: React.ReactNode;
  onBack: () => void;
  /** 등록된 공고 화면에서 그 공고 열기 */
  onOpenProgram: (id: string) => void;
  /** 검토 대기 건수가 바뀌면 (상단 배지) */
  onCount?: (n: number) => void;
}
type Status = ReviewStatus;
type Counts = { pending: number; done: number };
interface Undo { id: string; snap: GovTargetSnap; row: GovTargetRow; index: number; status: Status; gen: number }

const errMsg = (e: unknown, d: string) => (e instanceof Error && e.message ? e.message : d);
const OVERSCAN = 900;         // 화면 위아래로 미리 그려 둘 높이(px)
const GAP = 8;                // 줄 사이

/* ── 알림(토스트) — 따로 떨어진 부품: 뜨고 사라져도 목록은 그대로 ── */
interface ToastApi { show: (msg: string, withUndo?: boolean) => void; hide: () => void }
const Toast = forwardRef<ToastApi, { onUndo: () => void; onExpire: () => void }>(function Toast({ onUndo, onExpire }, ref) {
  const [t, setT] = useState<{ msg: string; undo: boolean; n: number } | null>(null);
  const expire = useRef(onExpire); expire.current = onExpire;
  useImperativeHandle(ref, () => ({
    show: (msg, withUndo = false) => setT(p => ({ msg, undo: withUndo, n: (p ? p.n : 0) + 1 })),
    hide: () => setT(null),
  }), []);
  useEffect(() => {
    if (!t) return;
    const h = setTimeout(() => { setT(null); expire.current(); }, t.undo ? 6000 : 3000);
    return () => clearTimeout(h);
  }, [t]);
  if (!t) return null;
  return (
    <div role="status" style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', maxWidth: 'calc(100vw - 32px)', padding: '10px 18px', borderRadius: R.md, background: C.text, color: C.white, fontSize: T.sm, fontWeight: 600, zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', gap: 12 }}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{t.msg}</span>
      {t.undo && <button type="button" onClick={onUndo} style={{ flexShrink: 0, border: 'none', background: 'transparent', color: '#93C5FD', fontWeight: 700, fontSize: T.sm, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>되돌리기 Z</button>}
    </div>
  );
});

/* ── 창 그리기 한 칸 — 실제 높이를 재서 알려 준다 ── */
function Slot({ id, ro, children }: { id: string; ro: ResizeObserver | null; children: React.ReactNode }) {
  const el = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const d = el.current;
    if (!d || !ro) return;
    ro.observe(d);
    return () => ro.unobserve(d);
  }, [ro]);
  return <div ref={el} data-slot={id} style={{ paddingBottom: GAP }}>{children}</div>;
}

export default function GovTargetReview({ topLeft, onBack, onOpenProgram, onCount }: Props) {
  const [status, setStatus] = useState<Status>('pending');
  const [rows, setRowsState] = useState<GovTargetRow[]>([]);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');
  const [cursor, setCursorState] = useState<string | null>(null);
  const [aiBusy, setAiBusy] = useState<string | null>(null);   // 「AI 다시 읽기」 중인 공고
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const toastRef = useRef<ToastApi | null>(null);

  // 최신 값 — 손놀림(고정 객체)과 키보드가 읽는다
  const rowsRef = useRef<GovTargetRow[]>([]);
  const cursorRef = useRef<string | null>(null);
  const statusRef = useRef<Status>('pending');
  const undoRef = useRef<Undo | null>(null);
  const aiBusyRef = useRef<string | null>(null);
  const genRef = useRef(0);   // 목록을 새로 읽을 때마다 +1 — 그 전에 보낸 저장의 응답은 화면에 덮지 않는다
  statusRef.current = status;
  const setRows = useCallback((next: GovTargetRow[]) => { rowsRef.current = next; setRowsState(next); }, []);
  const setCursor = useCallback((id: string | null) => { if (cursorRef.current === id) return; cursorRef.current = id; setCursorState(id); }, []);
  const toast = useCallback((msg: string, withUndo = false) => toastRef.current?.show(msg, withUndo), []);
  /** 검토 대기 ↔ 완료 건수 옮기기 (dir +1 = 지금 탭에서 한 줄 빠짐) */
  const moveCount = useCallback((st: Status, dir: 1 | -1) => setCounts(cn => (cn ? (st === 'pending' ? { pending: cn.pending - dir, done: cn.done + dir } : { pending: cn.pending + dir, done: cn.done - dir }) : cn)), []);

  /* ── 저장 줄 세우기 — 같은 공고는 차례대로 ── */
  const tails = useRef(new Map<string, Promise<void>>());
  const inflight = useRef(new Map<string, number>());
  const enqueue = useCallback((id: string, job: () => Promise<void>) => {
    inflight.current.set(id, (inflight.current.get(id) || 0) + 1);
    const prev = tails.current.get(id) || Promise.resolve();
    const run = prev.then(job, job).finally(() => {
      const n = (inflight.current.get(id) || 1) - 1;
      if (n <= 0) inflight.current.delete(id); else inflight.current.set(id, n);
      if (tails.current.get(id) === run) tails.current.delete(id);
    });
    tails.current.set(id, run);
  }, []);
  const allIdle = useCallback(async () => { while (tails.current.size) { const ps: Promise<void>[] = []; tails.current.forEach(p => ps.push(p)); await Promise.all(ps.map(p => p.catch(() => {}))); } }, []);

  const load = useCallback(async (st: Status, silent = false) => {
    genRef.current += 1;
    const gen = genRef.current;
    if (!silent) { setLoading(true); setLoadErr(''); }
    try {
      const r = await api.getGovTargetReview(st);   // 서버 기본 = 대기 목록 전부(최대 2,000 · 창 그리기라 줄 수가 많아도 가볍다)
      if (!r.success || !r.data) throw new Error(r.error || '목록을 불러오지 못했습니다');
      if (gen !== genRef.current) return;
      const list = r.data.rows;
      setRows(list); setCounts(r.data.counts);
      const c = cursorRef.current;
      setCursor(c && list.some(x => x.id === c) ? c : list[0]?.id || null);
    } catch (e) { if (gen === genRef.current) { if (silent) toast(errMsg(e, '목록을 불러오지 못했습니다')); else setLoadErr(errMsg(e, '목록을 불러오지 못했습니다')); } }
    if (gen === genRef.current && !silent) setLoading(false);
  }, [setRows, setCursor, toast]);
  useEffect(() => { undoRef.current = null; toastRef.current?.hide(); void load(status); }, [status, load]);
  useEffect(() => { if (counts && onCount) onCount(counts.pending); }, [counts, onCount]);

  /* ── 일괄 확인 (2026-10-07 지도사 「검토 대기 일괄로 한 번에 본 걸로」 · 1번 안) — 직접 값과 다름 · 확신 낮음은 남기고 나머지를 「본 것」으로 · 묶음째 되돌리기 ── */
  const [bulk, setBulk] = useState<api.GovTargetBulkPlan | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const loadBulk = useCallback(async () => { try { const r = await api.getGovTargetBulk(); if (r.success && r.data) setBulk(r.data); } catch { /* 없어도 검토는 된다 */ } }, []);
  useEffect(() => { void loadBulk(); }, [loadBulk, counts?.pending]);
  const runBulk = useCallback(async () => {
    if (!bulk || !bulk.confirm || bulkBusy) return;
    const keepN = bulk.keep.diff + bulk.keep.low;
    if (!window.confirm(`검토 대기 ${bulk.confirm.toLocaleString()}건을 「본 것」으로 처리합니다.\n켜진 값 · 뺀 값은 그대로이고, 학습 기록은 남기지 않습니다.\n\n남기는 공고 ${keepN.toLocaleString()}건 — 직접 정한 값과 다름 ${bulk.keep.diff} · 확신 낮음 ${bulk.keep.low}\n(나중에 「되돌리기」로 이 묶음만 다시 검토 대기로 돌릴 수 있습니다)`)) return;
    setBulkBusy(true);
    try {
      const r = await api.runGovTargetBulk();
      if (!r.success || !r.data) throw new Error(r.error || '일괄 확인 실패');
      toast(`${r.data.confirmed.toLocaleString()}건을 확인했습니다`);
      await load(statusRef.current, true); await loadBulk();
    } catch (e) { toast(errMsg(e, '일괄 확인 실패')); }
    setBulkBusy(false);
  }, [bulk, bulkBusy, toast, load, loadBulk]);
  const undoBulk = useCallback(async () => {
    if (!bulk || !bulk.last || bulkBusy) return;
    if (!window.confirm(`마지막 일괄 확인(${bulk.last.n.toLocaleString()}건)을 되돌려 다시 검토 대기로 올립니다.`)) return;
    setBulkBusy(true);
    try {
      const r = await api.undoGovTargetBulk(bulk.last.batch);
      if (!r.success || !r.data) throw new Error(r.error || '되돌리기 실패');
      toast(`${r.data.restored.toLocaleString()}건을 다시 검토 대기로 올렸습니다`);
      await load(statusRef.current, true); await loadBulk();
    } catch (e) { toast(errMsg(e, '되돌리기 실패')); }
    setBulkBusy(false);
  }, [bulk, bulkBusy, toast, load, loadBulk]);
  /** 저장이 실패하면 — 밀린 저장이 다 끝난 뒤 서버 값으로 목록을 다시 읽는다(스크롤 · 커서는 그대로) */
  const resync = useCallback(async () => { await allIdle(); await load(statusRef.current, true); }, [allIdle, load]);

  /** 서버 응답 줄 → 화면 (그 공고에 밀린 저장이 없을 때만 · 대기/완료 탭을 오가야 하면 넣거나 뺀다) */
  const reconcile = useCallback((srv: GovTargetRow, st: Status, gen: number, index: number) => {
    if (gen !== genRef.current || (inflight.current.get(srv.id) || 0) > 1) return;   // 이 저장 뒤에 또 저장이 밀려 있으면 마지막 응답이 맞춘다
    const list = rowsRef.current;
    const i = list.findIndex(x => x.id === srv.id);
    const leaves = st === 'pending' ? srv.reviewed : !srv.reviewed;
    if (i >= 0) {
      if (leaves) {
        const rest = list.filter(x => x.id !== srv.id); setRows(rest); moveCount(st, 1);
        if (cursorRef.current === srv.id) setCursor(rest[Math.min(i, rest.length - 1)]?.id || null);
      } else if (JSON.stringify(list[i]) !== JSON.stringify(srv)) { const cp = list.slice(); cp[i] = srv; setRows(cp); }
    } else if (!leaves) {
      const cp = list.slice(); cp.splice(Math.min(index, cp.length), 0, srv); setRows(cp); moveCount(st, -1);
    }
  }, [setRows, setCursor, moveCount]);

  /** 동작 → 화면에 바로 반영 → 서버 저장(뒤에서) → 응답으로 맞춤 (검토가 끝난 줄은 대기 목록에서 빠지고 커서는 다음 줄로) */
  const act = useCallback((id: string, body: ReviewBody, msg: string) => {
    const list = rowsRef.current;
    const index = list.findIndex(x => x.id === id);
    if (index < 0) return;
    const before = list[index];
    const st = statusRef.current, gen = genRef.current;
    const next = applyReviewAction(before, body);
    const leaves = st === 'pending' ? next.reviewed : !next.reviewed;
    if (leaves) {
      const rest = list.filter(x => x.id !== id);
      setRows(rest); moveCount(st, 1);
      if (cursorRef.current === id || !cursorRef.current) setCursor(rest[Math.min(index, rest.length - 1)]?.id || null);
    } else { const cp = list.slice(); cp[index] = next; setRows(cp); }
    const u: Undo = { id, snap: snapOf(before), row: before, index, status: st, gen };
    undoRef.current = u;
    toast(msg, true);
    enqueue(id, async () => {
      try {
        const r = await api.reviewGovTarget(id, body);
        if (!r.success || !r.data) throw new Error(r.error || '저장하지 못했습니다');
        reconcile(r.data.row, st, gen, index);
      } catch (e) {
        if (undoRef.current === u) undoRef.current = null;
        toast(errMsg(e, '저장하지 못했습니다'));
        void resync();
      }
    });
  }, [setRows, setCursor, moveCount, toast, enqueue, reconcile, resync]);

  const runUndo = useCallback(() => {
    const u = undoRef.current; if (!u) return;
    undoRef.current = null; toastRef.current?.hide();
    if (u.gen === genRef.current && u.status === statusRef.current) {
      const list = rowsRef.current;
      const i = list.findIndex(x => x.id === u.id);
      if (i >= 0) { const cp = list.slice(); cp[i] = u.row; setRows(cp); }
      else { const cp = list.slice(); cp.splice(Math.min(u.index, cp.length), 0, u.row); setRows(cp); moveCount(u.status, -1); }
      setCursor(u.id);
    }
    toast('되돌렸어요');
    const gen = genRef.current;
    enqueue(u.id, async () => {
      try {
        const r = await api.reviewGovTarget(u.id, { action: 'restore', snap: u.snap });
        if (!r.success || !r.data) throw new Error(r.error || '되돌리지 못했습니다');
        reconcile(r.data.row, u.status, gen, u.index);
      } catch (e) { toast(errMsg(e, '되돌리지 못했습니다')); void resync(); }
    });
  }, [setRows, setCursor, moveCount, toast, enqueue, reconcile, resync]);
  const clearUndo = useCallback(() => { undoRef.current = null; }, []);

  /** 「AI 다시 읽기」 — 공고문 전체를 AI 가 읽어 다시 판정 (2026-10-05o) · 오래 걸리면 뒤에서 계속 */
  const reread = useCallback(async (id: string) => {
    if (aiBusyRef.current) return;
    aiBusyRef.current = id; setAiBusy(id);
    const st = statusRef.current, gen = genRef.current;
    try {
      const r = await api.aiRereadGovTarget(id);
      if (!r.success || !r.data) throw new Error(r.error || 'AI 다시 읽기에 실패했습니다');
      if ('background' in r.data) { toast('AI 가 아직 읽는 중 — 잠시 뒤 새로고침하면 결과가 보입니다'); return; }
      const d = r.data;
      if (gen === genRef.current) {
        const list = rowsRef.current;
        const i = list.findIndex(x => x.id === id);
        const leaves = st === 'pending' ? d.row.reviewed : !d.row.reviewed;
        if (i >= 0 && leaves) { const rest = list.filter(x => x.id !== id); setRows(rest); moveCount(st, 1); if (cursorRef.current === id) setCursor(rest[Math.min(i, rest.length - 1)]?.id || null); }
        else if (i >= 0) { const cp = list.slice(); cp[i] = d.row; setRows(cp); }
      }
      const parts = [];
      if (d.added.length) parts.push(`켬 ${d.added.map(targetText).join(' · ')}`);
      if (d.removed.length) parts.push(`끔 ${d.removed.map(targetText).join(' · ')}`);
      if (d.bizxAdded.length) parts.push(`업종 제외 ${d.bizxAdded.join(' · ')}`);
      if (!parts.length) parts.push(d.changed ? '값이 바뀜' : '값은 그대로');
      toast(`AI 다시 읽기 — ${parts.join(' / ')} · ${CONF_LABEL[d.conf] || d.conf}${d.note ? ` · ${d.note}` : ''}`);
    } catch (e) { toast(errMsg(e, 'AI 다시 읽기에 실패했습니다')); }
    finally { aiBusyRef.current = null; setAiBusy(null); }
  }, [setRows, setCursor, moveCount, toast]);

  /* ── 줄 손놀림 — 한 번만 만든다(줄 memo 유지) ── */
  const find = (id: string) => rowsRef.current.find(x => x.id === id) || null;
  const onList = (row: GovTargetRow) => [...(row.subs || []), ...row.sec].map(targetText);
  const latest = useRef({ act, reread, onOpenProgram });
  latest.current = { act, reread, onOpenProgram };
  const handlers = useMemo<ReviewHandlers>(() => ({
    cursor: (id) => setCursor(id),
    open: (id) => latest.current.onOpenProgram(id),
    confirm: (id) => { const row = find(id); if (row) latest.current.act(id, { action: 'confirm' }, `「${row.name}」 맞음 — ${onList(row).join(' · ') || '세부업종 · 전용 대상 없음'}`); },
    none: (id) => { const row = find(id); if (row) latest.current.act(id, { action: 'none' }, `「${row.name}」 자동 체크 모두 아님 — 업종 조건대로 추천`); },
    reopen: (id) => { const row = find(id); if (row) latest.current.act(id, { action: 'restore', snap: { ...snapOf(row), reviewed: false } }, `「${row.name}」 다시 검토 대기로`); },
    /** 칩 누름 — 켜진 값은 뺌(아님) · 뺀 값 · 참고 값은 켬 */
    toggle: (id, k, turnOn) => { const t = targetText(k); latest.current.act(id, turnOn ? { action: 'include', k } : { action: 'exclude', k }, turnOn ? `${t} 켬` : `${t} 뺌 — 이 공고는 ${t} 아닌 업체에도 추천`); },
    reread: (id) => { void latest.current.reread(id); },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [setCursor]);

  /* ── 창 그리기 — 줄 높이를 재 가며 보이는 줄만 ── */
  const heights = useRef(new Map<string, number>());
  const [measureTick, setMeasureTick] = useState(0);
  const offsets = useMemo(() => {
    const off = new Array<number>(rows.length + 1);
    off[0] = 0;
    for (let i = 0; i < rows.length; i++) off[i + 1] = off[i] + (heights.current.get(rows[i].id) ?? estimateRowHeight(rows[i]));
    return off;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, measureTick]);
  const offRef = useRef(offsets); offRef.current = offsets;
  const [range, setRange] = useState<[number, number]>([0, 12]);
  const listTop = () => { const w = wrapRef.current, l = listRef.current; return w && l ? l.getBoundingClientRect().top - w.getBoundingClientRect().top + w.scrollTop : 0; };
  /** 어느 줄이 offset(px) 에 있나 */
  const indexAt = (off: number[], y: number) => { let lo = 0, hi = off.length - 2; if (hi < 0) return 0; while (lo < hi) { const mid = (lo + hi + 1) >> 1; if (off[mid] <= y) lo = mid; else hi = mid - 1; } return Math.max(0, lo); };
  const updateRange = useCallback(() => {
    const w = wrapRef.current; const off = offRef.current; const n = off.length - 1;
    if (!w || n <= 0) { setRange(r => (r[0] === 0 && r[1] === 0 ? r : [0, 0])); return; }
    const top = w.scrollTop - listTop();
    const a = indexAt(off, Math.max(0, top - OVERSCAN));
    const b = Math.min(n, indexAt(off, Math.max(0, top + w.clientHeight + OVERSCAN)) + 1);
    setRange(r => (r[0] === a && r[1] === b ? r : [a, b]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useLayoutEffect(() => { updateRange(); }, [offsets, updateRange]);
  useEffect(() => {
    const w = wrapRef.current; if (!w) return;
    let raf = 0;
    const onScroll = () => { if (raf) return; raf = requestAnimationFrame(() => { raf = 0; updateRange(); }); };
    w.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => { w.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [updateRange]);
  // 실제 높이 재기 — 화면 위쪽 줄의 높이가 바뀌면 그만큼 스크롤을 옮겨 보던 자리가 튀지 않게
  const [ro, setRo] = useState<ResizeObserver | null>(null);
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const obs = new ResizeObserver(entries => {
      let changed = false, shift = 0;
      const w = wrapRef.current; const off = offRef.current; const list = rowsRef.current;
      const viewTop = w ? w.scrollTop - listTop() : 0;
      for (const en of entries) {
        const el = en.target as HTMLElement;
        const id = el.dataset.slot || '';
        const h = el.offsetHeight;
        if (!id || !h) continue;
        const old = heights.current.get(id);
        const i = list.findIndex(x => x.id === id);
        const prevH = old ?? (i >= 0 ? estimateRowHeight(list[i]) : h);
        if (old === h) continue;
        heights.current.set(id, h);
        changed = true;
        if (i >= 0 && off[i] + prevH <= viewTop + 1) shift += h - prevH;   // 보고 있는 자리보다 위에 있는 줄
      }
      if (shift && w) w.scrollTop += shift;
      if (changed) setMeasureTick(t => t + 1);
    });
    setRo(obs);
    return () => obs.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 커서 줄이 보이게 (그려지지 않은 줄도 — 높이표로 스크롤을 옮긴다)
  //  어림 높이로 옮긴 뒤 줄이 그려져 실제 높이가 달라지면 잠깐(0.6초) 동안 다시 맞춘다 — 빠르게 ↓를 눌러도 커서 줄이 화면 밖으로 밀리지 않게
  const pinUntil = useRef(0);
  const ensureCursor = useCallback(() => {
    const w = wrapRef.current; const id = cursorRef.current; if (!id || !w) return;
    const i = rowsRef.current.findIndex(x => x.id === id); if (i < 0) return;
    const off = offRef.current; const lt = listTop();
    const top = lt + off[i], bottom = lt + off[i + 1] - GAP;
    const pad = 12;
    if (top - pad < w.scrollTop) w.scrollTop = Math.max(0, top - pad);
    else if (bottom + pad > w.scrollTop + w.clientHeight) w.scrollTop = Math.min(top - pad, bottom + pad - w.clientHeight);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { if (!cursor) return; pinUntil.current = performance.now() + 600; ensureCursor(); }, [cursor, ensureCursor]);
  useLayoutEffect(() => { if (performance.now() < pinUntil.current) ensureCursor(); }, [offsets, ensureCursor]);
  useEffect(() => {
    // 지도사가 직접 스크롤(휠 · 터치)하면 맞추기를 멈춘다
    const w = wrapRef.current; if (!w) return;
    const stop = () => { pinUntil.current = 0; };
    w.addEventListener('wheel', stop, { passive: true }); w.addEventListener('touchstart', stop, { passive: true });
    return () => { w.removeEventListener('wheel', stop); w.removeEventListener('touchstart', stop); };
  }, []);

  // 키보드 — ↑↓ 이동 · Y 맞음 · X 모두 아님 · 1~9 라벨 빼기/켜기 · Enter 공고 열기 · Z 되돌리기
  const keyRef = useRef<(e: KeyboardEvent) => void>(() => {});
  keyRef.current = (e: KeyboardEvent) => {
    const tag = (e.target as HTMLElement | null)?.tagName || '';
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.metaKey || e.ctrlKey || e.altKey) return;
    const list = rowsRef.current; const st = statusRef.current;
    const i = list.findIndex(x => x.id === cursorRef.current);
    const row = i >= 0 ? list[i] : null;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!list.length) return;
      const n = i < 0 ? 0 : Math.max(0, Math.min(list.length - 1, i + (e.key === 'ArrowDown' ? 1 : -1)));
      setCursor(list[n].id); return;
    }
    if (e.code === 'KeyZ' && undoRef.current) { e.preventDefault(); runUndo(); return; }
    if (!row) return;
    if (e.key === 'Enter') { e.preventDefault(); onOpenProgram(row.id); return; }
    if (st === 'pending' && e.code === 'KeyY') { e.preventDefault(); handlers.confirm(row.id); return; }
    if (st === 'pending' && e.code === 'KeyX') { e.preventDefault(); handlers.none(row.id); return; }
    if (st === 'done' && e.code === 'KeyR') { e.preventDefault(); handlers.reopen(row.id); return; }
    if (e.code === 'KeyA') { e.preventDefault(); handlers.reread(row.id); return; }
    const d = /^Digit([1-9])$/.exec(e.code);
    if (d) { const it = row.items[Number(d[1]) - 1]; if (it && !it.inAny) { e.preventDefault(); handlers.toggle(row.id, it.k, it.off || isPendingHint(it)); } }
  };
  useEffect(() => {
    const h = (e: KeyboardEvent) => keyRef.current(e);
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  // 대기 목록을 다 비웠는데 서버에 남은 게 있으면(한 번에 못 받은 몫) 다시 받는다
  const refilled = useRef(false);
  useEffect(() => {
    if (loading || rows.length || !counts) { if (rows.length) refilled.current = false; return; }
    const left = status === 'pending' ? counts.pending : counts.done;
    if (left > 0 && !refilled.current) { refilled.current = true; void resync(); }
  }, [loading, rows.length, counts, status, resync]);

  const seg = (k: Status, label: string, n: number | undefined) => {
    const on = status === k;
    return (
      <button key={k} type="button" role="tab" aria-selected={on} onClick={() => setStatus(k)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
          background: on ? C.white : 'transparent', boxShadow: on ? '0 1px 2px rgba(17,24,39,0.10)' : 'none', color: on ? C.text : C.sub, fontSize: T.sm, fontWeight: on ? 700 : 500 }}>
        {label}{n != null && <span style={{ fontSize: T.xs, fontWeight: 700, color: on ? (k === 'pending' && n ? '#B45309' : C.accent) : C.muted, fontVariantNumeric: 'tabular-nums' }}>{n.toLocaleString()}</span>}
      </button>
    );
  };
  const empty = !loading && !loadErr && rows.length === 0;
  const keysHint = status === 'pending'
    ? [['↑↓', '이동'], ['Y', '맞음'], ['X', '모두 아님'], ['1~9', '하나 빼기·켜기'], ['A', 'AI 다시 읽기'], ['Enter', '공고 열기']]
    : [['↑↓', '이동'], ['R', '다시 검토'], ['1~9', '켜기·빼기'], ['A', 'AI 다시 읽기'], ['Enter', '공고 열기']];

  const header = (
    <div className="gtr-head">
      <div style={{ minWidth: 0 }}>
        <button type="button" className="gtr-back" onClick={onBack} title="등록된 공고 목록으로"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: 0, marginBottom: 6, border: 'none', background: 'none', color: C.accent, fontSize: T.sm, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
          ‹ 등록된 공고<span>로 돌아가기</span>
        </button>
        <div style={{ fontSize: T.lg, fontWeight: 700, color: C.text }}>세부업종 · 전용 대상 자동 체크 검토</div>
        <div style={{ fontSize: T.sm, color: C.sub, marginTop: 3, lineHeight: 1.5 }}>
          AI 가 공고문 전체를 읽고 «이 분야·자격만 신청 가능»으로 판정해 켠 값입니다(근거 인용문 · 확신). 켜진 값은 매칭에 바로 쓰이고, 아니면 빼 주세요. 흐린 「참고」는 낱말로만 잡힌 값이라 켜져 있지 않습니다.
        </div>
      </div>
      <div role="tablist" style={{ display: 'inline-flex', padding: 3, gap: 2, borderRadius: R.md, background: '#E9EBEF', flexShrink: 0, alignSelf: 'flex-start' }}>
        {seg('pending', '검토 대기', counts?.pending)}
        {seg('done', '검토 완료', counts?.done)}
      </div>
    </div>
  );

  const [a, b] = range;
  const total = offsets[rows.length] || 0;
  const visible = rows.slice(a, Math.min(b, rows.length));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
      <style>{`
        .gtr-wrap{flex:1;min-height:0;overflow-y:auto;overflow-anchor:none;padding:16px 20px 90px}
        .gtr-in{max-width:1080px;margin:0 auto}
        .gtr-head{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;margin-bottom:12px}
        .gtr-bulk{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:12px;padding:10px 14px;border:1px solid ${C.border};border-radius:${R.md}px;background:${C.bgAlt}}
        .gtr-keys{display:flex;flex-wrap:wrap;gap:4px 12px;margin-bottom:12px;font-size:11px;color:${C.muted}}
        .gtr-keys kbd{font-family:inherit;font-size:10.5px;font-weight:700;color:${C.sub};background:${C.white};border:1px solid ${C.border};border-bottom-width:2px;border-radius:4px;padding:0 5px;margin-right:4px}
        .gtr-row{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:8px 16px;padding:12px 14px;border:1px solid ${C.border};border-radius:${R.md}px;background:${C.white};cursor:pointer;transition:border-color .12s, box-shadow .12s}
        .gtr-row:hover{border-color:${C.borderDk}}
        .gtr-row.cur{border-color:${C.accent};box-shadow:0 0 0 3px ${C.accentLt}}
        .gtr-acts{display:flex;flex-direction:column;gap:6px;align-items:stretch;min-width:112px}
        .gtr-btn{display:inline-flex;align-items:center;justify-content:center;gap:6px;padding:6px 12px;border-radius:${R.sm}px;font-size:12px;font-weight:600;font-family:inherit;cursor:pointer;white-space:nowrap;min-height:32px}
        .gtr-btn kbd{font-family:inherit;font-size:10px;font-weight:700;opacity:.7;border:1px solid currentColor;border-radius:3px;padding:0 3px;line-height:13px}
        .gtr-ev{display:flex;flex-direction:column;gap:3px;margin-top:8px;font-size:12px;line-height:1.55}
        .gtr-evl{min-width:0;word-break:keep-all;overflow-wrap:anywhere}
        .gtr-from{display:inline-block;font-size:11px;color:${C.muted};border:1px solid ${C.border};border-radius:4px;padding:0 5px;margin-right:6px;line-height:16px;vertical-align:1px;white-space:nowrap}
        .gtr-chip{display:inline-flex;align-items:center;gap:4px;border-radius:6px;font-size:12px;line-height:1.35;padding:3px 4px 3px 9px;font-family:inherit;white-space:nowrap}
        .gtr-chip .x{display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:4px;border:none;background:transparent;color:inherit;cursor:pointer;font-size:12px;line-height:1;padding:0;opacity:.7}
        .gtr-chip .x:hover{opacity:1;background:rgba(0,0,0,.06)}
        .gtr-chip .n{font-size:10px;font-weight:700;opacity:.55;margin-right:1px}
        .gtr-grp{display:flex;flex-wrap:wrap;align-items:center;gap:5px;margin-top:8px}
        .gtr-grp>.gl{flex:none;width:64px;font-size:11px;font-weight:600;color:${C.muted}}
        .gtr-ai{display:flex;flex-wrap:wrap;align-items:center;gap:4px 8px;margin-top:8px;padding:6px 9px;border-radius:${R.sm}px;background:${C.bgAlt};border:1px solid ${C.border};font-size:11.5px;color:${C.sub};line-height:1.5}
        .gtr-chip.hint{opacity:.62}
        .gtr-chip .go{display:inline-flex;align-items:center;height:18px;padding:0 6px;border-radius:4px;border:1px solid currentColor;background:transparent;color:inherit;cursor:pointer;font-size:10.5px;font-weight:700;line-height:1;font-family:inherit;opacity:.8}
        .gtr-chip .go:hover{opacity:1;background:rgba(0,0,0,.06)}
        @media (max-width:640px){
          .gtr-wrap{padding:12px 12px 90px}
          .gtr-head{flex-direction:column;gap:10px}
          .gtr-keys{display:none}
          .gtr-bulk{flex-direction:column;align-items:stretch} .gtr-bulk .gtr-btn{flex:1;min-height:38px}
          .gtr-row{grid-template-columns:minmax(0,1fr);padding:12px}
          .gtr-acts{flex-direction:row;min-width:0}
          .gtr-acts .gtr-btn{flex:1;min-height:38px}
          .gtr-btn kbd{display:none}
          .gtr-name{white-space:normal!important;display:-webkit-box!important;-webkit-line-clamp:2;-webkit-box-orient:vertical}
          .gtr-back span{display:none}
        }
      `}</style>
      <GovTopBar left={topLeft} />
      <div className="gtr-wrap" ref={wrapRef}>
        <div className="gtr-in">
          {header}
          {bulk && (bulk.confirm > 0 || bulk.last) && (
            <div className="gtr-bulk">
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: T.sm, fontWeight: 700, color: C.text }}>한 번에 확인</div>
                <div style={{ fontSize: T.xs, color: C.sub, marginTop: 2, lineHeight: 1.5 }}>
                  {bulk.confirm > 0
                    ? <>값이 바뀐 공고 등 {bulk.confirm.toLocaleString()}건을 「본 것」으로 · 직접 정한 값과 다름 {bulk.keep.diff.toLocaleString()} · 확신 낮음 {bulk.keep.low.toLocaleString()}건은 남깁니다</>
                    : <>한 번에 확인할 공고가 없습니다 — 남은 공고는 직접 정한 값과 다르거나 확신이 낮은 것입니다</>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap' }}>
                {bulk.last && <button type="button" className="gtr-btn" disabled={bulkBusy} onClick={() => void undoBulk()} style={{ border: `1px solid ${C.border}`, background: C.white, color: C.sub }}>되돌리기 ({bulk.last.n.toLocaleString()}건)</button>}
                {bulk.confirm > 0 && <button type="button" className="gtr-btn" disabled={bulkBusy} onClick={() => void runBulk()} style={{ border: 'none', background: C.accent, color: C.white, opacity: bulkBusy ? 0.6 : 1 }}>{bulkBusy ? '처리 중…' : `${bulk.confirm.toLocaleString()}건 확인`}</button>}
              </div>
            </div>
          )}
          <div className="gtr-keys" aria-hidden>{keysHint.map(([k, t]) => <span key={k}><kbd>{k}</kbd>{t}</span>)}</div>
          {loadErr && <div style={{ padding: '10px 12px', borderRadius: R.sm, background: C.redLt, color: C.red, fontSize: T.sm, marginBottom: 10 }}>{loadErr} <button type="button" onClick={() => void load(status)} style={{ marginLeft: 8, border: 'none', background: 'none', color: C.red, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>다시</button></div>}
          {loading && !rows.length && <div style={{ padding: 30, textAlign: 'center', color: C.muted, fontSize: T.sm }}>불러오는 중…</div>}
          {empty && (
            <div style={{ padding: '36px 16px', textAlign: 'center', border: `1px dashed ${C.border}`, borderRadius: R.md, background: C.bgAlt }}>
              <div style={{ fontSize: T.md, fontWeight: 700, color: C.text }}>{status === 'pending' ? '검토할 공고가 없습니다' : '검토를 마친 공고가 아직 없습니다'}</div>
              <div style={{ fontSize: T.sm, color: C.sub, marginTop: 4 }}>{status === 'pending' ? '새 공고를 등록하거나 검토함에서 승인하면 세부업종 · 전용 대상을 자동으로 찾아 여기에 올립니다' : '「검토 대기」에서 맞음 · 아님을 고르면 여기로 옮겨집니다'}</div>
            </div>
          )}
          <div ref={listRef} role="list" aria-label={status === 'pending' ? '검토 대기 공고' : '검토 완료 공고'} style={{ position: 'relative', height: total }}>
            <div style={{ position: 'absolute', left: 0, right: 0, top: offsets[a] || 0 }}>
              {visible.map((row, j) => (
                <Slot key={row.id} id={row.id} ro={ro}>
                  <ReviewRow row={row} index={a + j} cur={row.id === cursor} status={status} aiBusy={aiBusy === row.id} h={handlers} />
                </Slot>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Toast ref={toastRef} onUndo={runUndo} onExpire={clearUndo} />
    </div>
  );
}
